'use strict'

var ipaddr = require('ipaddr.js')
var _ = require('lodash')

var singletonArray = []

// ipaddr.subnetsOverlap =
function subnetsOverlap (first, second) {
  if (!first || !second) {
    return false
  }

  // There is likely a more straightforward approach here.
  // I'm pulling the addresses out and converting to ipv6
  // then putting the mask back on
  var firstAddress = ipaddr.parse(first.split('/')[0])
  var firstMask = Number(first.split('/')[1])
  var secondAddress = ipaddr.parse(second.split('/')[0])
  var secondMask = Number(second.split('/')[1])

  // make all addresses ipv6
  if (firstAddress.kind() === 'ipv4') {
    firstAddress = firstAddress.toIPv4MappedAddress()
    // ipv4 segment of mapped addresses begins at the 97th bit
    firstMask += 96
  }

  if (secondAddress.kind() === 'ipv4') {
    secondAddress = secondAddress.toIPv4MappedAddress()
    secondMask += 96
  }

  var firstCIDR = ipaddr.parseCIDR(firstAddress.toString() + "/" + firstMask)
  var secondCIDR = ipaddr.parseCIDR(secondAddress.toString() + "/" + secondMask)

  return firstAddress.match(secondCIDR) || secondAddress.match(firstCIDR)
}

var createIpAddressUtil = function (useCache) {
  var cache = useCache ? Object.create(null) : null

  // Fork of ipaddr.parse() that does a more efficient pre-filter for IPv6 addresses,
  // so it doesn't attempt the (very!) expensive IPv6 parsing for the majority of IPs.
  // Note: This function should remain internal to this util, to isolate our dependency on ipaddr.js
  var _parseIp = function (string) {
    if (!string) { return null }
    var ipObj = cache ? cache[string] : undefined
    if (ipObj === undefined) {
      try {
        ipObj = string.indexOf(':') !== -1 ? ipaddr.IPv6.parse(string) : ipaddr.IPv4.parse(string)
      } catch (e) {
        ipObj = null
      }
    }
    if (cache) {
      cache[string] = ipObj
    }
    return ipObj
  }

  var isIpInSubnet = function (ip, subnet) {
    // var [subnetIp, subnetMask] = subnet.split('/')
    var _split = subnet.split('/')
    var subnetIp = _split[0]
    var subnetMask = _split[1]
    subnetIp = _parseIp(subnetIp)
    ip = _parseIp(ip)
    if (!subnetIp || !ip) { return false }
    if (subnetIp.kind() === ip.kind()) {
      subnetMask = +subnetMask
      if (isNaN(subnetMask)) { //default missing mask to match single IP
        subnetMask = subnetIp.kind() === 'ipv6' ? 128 : 32
      }
      return ip.match(subnetIp, subnetMask)
    }
    return false
  }

  var isValidIp = function (value, version) {
    var ip = _parseIp(value)
    if (ip) {
      return !_.isNumber(version) || (version === 4 && ip.kind() === 'ipv4') || (version === 6 && ip.kind() === 'ipv6')
    } else {
      return false
    }
  }

  var _testMask = function (ip, mask, ipv4) {
    if (ipv4) {
      return ip.kind() === 'ipv4' && mask >= 0 && mask <= 32
    } else {
      return ip.kind() === 'ipv6' && mask >= 0 && mask <= 128
    }
  }

  var isValidCIDR = function (value) {
    try {
      // var [ip, mask] = value.split('/')
      var _split = value.split('/')
      var ip = _parseIp(_split[0])
      var mask = _split[1]
      // ip = _parseIp(ip)
      if (ip == null) { return false }
      mask = /\d{1,3}/.test(mask) ? +mask : NaN
      if (!isNaN(mask) && (_testMask(ip, mask, true) || _testMask(ip, mask, false))) {
        return true
      }
    } catch (e) {
      return false
    }
    return false
  }

  var doSubnetsOverlap = function (subnet1, subnet2) {
    return subnetsOverlap(subnet1, subnet2)
  }

  var formatCIDR = function (ip, mask) {
    var tempMask = +mask
    var bytes = getBytes(ip)
    singletonArray.length = bytes.length
    for (var i = 0; i < bytes.length; i++) {
      if (tempMask === 0) {
        singletonArray[i] = 0
      } else if (tempMask < 8) {
        singletonArray[i] = bytes[i] >> (8 - tempMask) << (8 - tempMask)
        tempMask = 0
      } else {
        singletonArray[i] = bytes[i]
        tempMask -= 8
      }
    }
    return ipaddr.fromByteArray(singletonArray).toString() + '/' + mask
  }

  var getBytes = function (ip) {
    ip = _parseIp(ip)
    return ip ? ip.toByteArray() : null
  }

  var getParsedString = function (ip, toNormalized) {
    toNormalized = toNormalized || false
    ip = _parseIp(ip)
    return ip ? (toNormalized ? ip.toNormalizedString() : ip.toString()) : null
  }

  var getComponents = function (ip) {
    ip = _parseIp(ip)
    if (!ip) { return null }
    if (ip.octets) {
      return {
        ipv6: false,
        components: ip.octets
      }
    } else {
      return {
        ipv6: true,
        components: ip.parts
      }
    }
  }

  var isIpv6 = function (ip) {
    ip = _parseIp(ip)
    return ip ? ip.kind() === 'ipv6' : null
  }

  var isSpecialRange = function (ip) {
    ip = _parseIp(ip)
    var range = ip && ip.range()
    return range && range !== 'unicast' ? range : false
  }


  // Given an IP address, return a set of coordinates calculated by treating the IP's bits as
  // bisection operations through a rotating set of N dimensions. This essentially treats the IP address
  // like a GeoHash which we decode to give a unique position.
  //
  // Any arbitrary number of dimensions can be specified; it defaults to 2D (x/y).
  //
  // Each returned coordinate is in the range from 0 to `edgeLength`; the default edgeLength is 1.
  // Setting th edgeLength to a high power-of-two value can help guarantee whole number coordinates
  // without rounding issues, if the max bit depth is known.
  var getHashCoords = function (ip, dimensions, edgeLength) {
    dimensions = dimensions || 2
    edgeLength = edgeLength || 1
    var bytes = _.isArray(ip) ? ip : getBytes(ip) // allow passing in parsed bytes
    if (!bytes) { return null }
    var coords = []
    for (var h = 0; h < dimensions; h++) {
      coords[h] = edgeLength / 2
    }
    var dimensionIdx = 0
    var dist = edgeLength / 4
    for (var i = 0, _len = bytes.length; i < _len; i++) {
      var byte = bytes[i]
      for (var j = 0; j < 8; j++) {
        var dir = (byte & 128) ? 1 : -1 // is a 1 bit in the leading position?
        coords[dimensionIdx] += dist * dir
        byte = byte << 1 // shift the next bit into position
        if (++dimensionIdx === dimensions) {
          dimensionIdx = 0
          dist /= 2
        }
      }
    }
    return coords
  }

  /**
   * Determine if an IP address is in a reserved address space
   *
   * @see https://github.com/indutny/node-ip/blob/master/lib/ip.js
   * @param addr {string} IP Address as a string (xxx.xxx.xxx.xxx)
   * @returns {boolean}
   */
  function isPrivateIP (addr) {
    if (_.isEmpty(addr)) { return false; }

    // Check IPv4 mapped IPv6 addresses
    if (/^::ffff:(\d{1,3}\.){3,3}\d{1,3}$/.test(addr)) {
      addr = addr.replace(/^::ffff:/, '');
    }

    return /^10\.([0-9]{1,3})\.([0-9]{1,3})\.([0-9]{1,3})/.test(addr) ||
      /^192\.168\.([0-9]{1,3})\.([0-9]{1,3})/.test(addr) ||
      /^172\.(1[6-9]|2\d|30|31)\.([0-9]{1,3})\.([0-9]{1,3})/.test(addr) ||
      /^127\.([0-9]{1,3})\.([0-9]{1,3})\.([0-9]{1,3})/.test(addr) ||
      /^169\.254\.([0-9]{1,3})\.([0-9]{1,3})/.test(addr) ||
      /^fc00:/.test(addr) ||
      /^fe80:/.test(addr) ||
      /^::1$/.test(addr) ||
      /^::$/.test(addr);
  }

  return {
    cache: cache,
    isIpInSubnet: isIpInSubnet,
    doSubnetsOverlap: doSubnetsOverlap,
    isValidCIDR: isValidCIDR,
    isValidIp: isValidIp,
    formatCIDR: formatCIDR,
    getBytes: getBytes,
    getHashCoords: getHashCoords,
    getParsedString: getParsedString,
    getComponents: getComponents,
    isIpv6: isIpv6,
    isPrivateIP: isPrivateIP,
    isSpecialRange: isSpecialRange
  }
}

// var ipAddressUtil = createIpAddressUtil(false)
//
// ipAddressUtil.batch = () => { return createIpAddressUtil(true) }

module.exports = _.assign(ipaddr, {
  // Default singleton, without caching
  util: createIpAddressUtil(false),

  // Calling ipAddressUtil.batch() returns a new instance of the util object that utilizes an
  // internal cache of parsed IP address objects over its lifetime, until it gets garbage collected.
  // This can greatly improve performance when performing operations over a large number of
  // IPs/subnets in a loop, since the IP parsing is expensive.
  batch: function () {
    return createIpAddressUtil(true)
  },

  // Export as standalone method on extended ipaddr.js instance
  subnetsOverlap: subnetsOverlap
})
